import matter from 'gray-matter';
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import remarkGfm from 'remark-gfm';
import { BlogPost, BlogFrontmatter } from '@/types/blog';
import yaml from 'js-yaml';

// Process markdown content to HTML
export async function markdownToHtml(markdown: string): Promise<string> {
  const result = await remark().use(remarkGfm).use(remarkHtml).process(markdown);
  return result.toString();
}

// Parse markdown file with frontmatter
export function parseMarkdownFile(fileContent: string): {
  frontmatter: BlogFrontmatter;
  content: string;
} {
  // gray-matter has issues with complex objects; let's simplify parsing
  const { data, content } = matter(fileContent, {
    engines: {
      yaml: {
        parse: (s: string) => yaml.load(s, { schema: yaml.JSON_SCHEMA }) as object,
        stringify: (d: object) => yaml.dump(d)
      }
    }
  });

  return {
    frontmatter: data as BlogFrontmatter,
    content
  };
}

// Convert parsed data to BlogPost
export async function createBlogPost(
  frontmatter: BlogFrontmatter,
  content: string
): Promise<BlogPost> {
  const htmlContent = await markdownToHtml(content);
  
  return {
    ...frontmatter,
    content: htmlContent
  };
}

// Format date for display
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Generate reading time estimate
export function calculateReadingTime(content: string): string {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} min read`;
}

// Sort posts by date (newest first)
export function sortPostsByDate(posts: BlogPost[]): BlogPost[] {
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

// Get featured posts
export function getFeaturedPosts(posts: BlogPost[]): BlogPost[] {
  return posts.filter(post => post.featured);
}
